import { useMemo, useRef } from "react";

import type Highcharts from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";

import { combineConfigs } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";

import { useMarketPlayersData } from "app/pages/industry-analysis/players/hooks/useMarketPlayersData";
import { chartTypes } from "app/UtilitiesAndConstants/Constants/ChartTypes";
import Chart from "pro-features/components/Chart/src/Chart";
import { CustomLabel } from "pro-features/components/Chart/src/components/column/CustomLabel";
import type { EChartDisplayType } from "pro-features/components/Chart/src/configs/column-chart/columnChartConfig";
import { renderIcon } from "pro-features/components/Chart/src/dataLabels/render-custom-labels/renderIcon";
import type { ColumnChartDataItems } from "pro-features/components/Chart/src/types";

import { MarketSharesSinglePeriodTooltipFormatter } from "./MarketSharesSinglePeriodTooltipFormatter";
import type { SwitchGraphDomainData } from "./types";
import { getAdjacentPeriodChangeById, getDomainSubtitle } from "./utils/utils";

const defaultConfig: Highcharts.Options = {
    legend: {
        enabled: false,
    },
    xAxis: {
        crosshair: false,
        lineColor: colorsPalettes.carbon[200],
        gridLineWidth: 0,
        tickLength: 5,
        categories: [],
    },
    yAxis: {
        labels: {
            style: {
                fontSize: "11px",
                fontFamily: "Roboto",
                color: colorsPalettes.carbon[400],
            },
        },
        gridLineWidth: 0.5,
        showFirstLabel: true,
        showLastLabel: true,
        gridZIndex: 2,
        tickPixelInterval: 65,
    },
    chart: {
        type: chartTypes.COLUMN,
        animation: false,
        borderColor: colorsPalettes.carbon[0],
        height: 350,
        marginTop: 40,
    },
    plotOptions: {
        series: {
            states: {
                hover: {
                    enabled: false,
                },
            },
        },
        column: {
            grouping: true,
            maxPointWidth: 32,
            minPointLength: 3,
            pointPadding: 0.1,
            borderRadius: 4,
        },
    },
};

type PointWithMetadata = {
    regionalDomainsCount: number;
    displayText: string;
    favicon: string;
} & Highcharts.Point;

interface Props {
    chartData: ColumnChartDataItems;
    chartDisplayType: EChartDisplayType;
    labelFormatter: (value: number) => string;
    maxYaxisValue: number | null;
    domains: SwitchGraphDomainData[];
    config?: Highcharts.Options;
}

export const MarketSharesSinglePeriod = ({
    chartData,
    labelFormatter,
    chartDisplayType,
    maxYaxisValue,
    domains,
    config = {},
}: Props) => {
    const containerID = "market-shares-chart-container";

    // Add className to each data point for renderIcon to work
    const enrichedChartData = chartData.map((dataItem) => ({
        ...dataItem,
        className: String(dataItem.id),
    }));

    const categories = enrichedChartData.map((point) => point.displayText);

    // Create stable key based on data to force remount when data changes
    const chartKey = `${chartDisplayType}-${enrichedChartData.map((d) => d.id).join("-")}`;

    const chartRef = useRef<Highcharts.Chart | null>(null);

    const { data: marketPlayersData } = useMarketPlayersData();
    const adjacentPeriodChangeById = useMemo(
        () =>
            getAdjacentPeriodChangeById(
                marketPlayersData?.risingAndDecliningRawData?.Data,
                marketPlayersData?.rawData?.Data,
            ),
        [marketPlayersData],
    );

    const finalConfig = combineConfigs({}, [
        defaultConfig,
        config,
        {
            chart: {
                events: {
                    load: function () {
                        // eslint-disable-next-line @typescript-eslint/no-this-alias
                        const chart = this;
                        const xAxis = chart.xAxis[0];

                        xAxis.update({
                            labels: {
                                useHTML: true,
                                formatter: function () {
                                    const categoryIndex = this.pos;
                                    const chartDataItem = enrichedChartData[categoryIndex];
                                    return renderToStaticMarkup(
                                        <CustomLabel
                                            displayText={chartDataItem.displayText}
                                            subDisplayText={chartDataItem.subDisplayText}
                                            value={labelFormatter(chartDataItem.y)}
                                        />,
                                    );
                                },
                            },
                        });

                        renderIcon(containerID)(chart);
                    },
                    redraw: function () {
                        renderIcon(containerID)(this);
                    },
                },
            },
            xAxis: { categories },
            yAxis: {
                max: maxYaxisValue,
                labels: {
                    formatter: function () {
                        return labelFormatter(this.value as number);
                    },
                },
            },
            tooltip: {
                enabled: true,
                useHTML: true,
                shared: true,
                outside: true,
                formatter: function () {
                    const currentPoint = this as PointWithMetadata;
                    const currentPointId = enrichedChartData[currentPoint.index].id;
                    const currentPointValue = enrichedChartData[currentPoint.index].y;
                    const regionalDomainsCount =
                        enrichedChartData[currentPoint.index].regionalDomainsCount ?? currentPoint.regionalDomainsCount;
                    const subDisplayText = getDomainSubtitle(undefined, regionalDomainsCount);
                    const currentDomain = domains.find((d) => d.Id === currentPointId);

                    // For "Others", calculate the total market and absolute visits
                    let totalMarketVisits: number | undefined;
                    if (!currentDomain && domains && domains.length > 0) {
                        totalMarketVisits = domains.reduce((sum, domain) => {
                            const metrics = domain.Metrics?.[0];
                            const avgVisits = metrics?.TotalMetrics?.AvgMonthVisits;
                            return sum + (avgVisits || 0);
                        }, 0);
                    }

                    return renderToStaticMarkup(
                        <MarketSharesSinglePeriodTooltipFormatter
                            websiteTitle={currentPoint.displayText}
                            websiteIcon={currentPoint.favicon}
                            subDisplayText={subDisplayText}
                            currentValue={currentPointValue}
                            rawDomainData={currentDomain}
                            chartDisplayType={chartDisplayType}
                            totalMarketVisits={totalMarketVisits}
                            adjacentPeriodChange={adjacentPeriodChangeById.get(currentPointId)}
                        />,
                    );
                },
            },
        },
    ]);

    return (
        <div id={containerID}>
            <Chart
                type={chartTypes.COLUMN}
                data={[{ data: enrichedChartData }]}
                config={finalConfig}
                key={chartKey}
                afterRender={(chart) => (chartRef.current = chart)}
            />
        </div>
    );
};
